import type { JSX } from 'react';
import { useState } from 'react';
import type { WireSourceField } from '@logistics/contracts';
import { cn } from '@/lib/utils';
import { input } from '../shell/look';

const OTHER = '__other__';

/**
 * A select from the descriptor's options. With `allowCustom`, a last "Other…"
 * choice opens a text box for a value the list does not have — a company's own
 * mail server — and a stored value outside the list opens there too.
 */
export function SelectField({
  id,
  field,
  value,
  onChange,
}: {
  id: string;
  field: WireSourceField;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  const options = field.options ?? [];
  const [custom, setCustom] = useState(
    field.allowCustom === true && value !== '' && !options.includes(value),
  );
  const control = cn(input, 'font-mono text-[13.5px]');

  return (
    <div className="flex flex-col gap-2">
      <select
        id={id}
        value={custom ? OTHER : value}
        className={control}
        onChange={(e) => {
          const next = e.target.value;
          setCustom(next === OTHER);
          onChange(next === OTHER ? '' : next);
        }}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
        {field.allowCustom === true && <option value={OTHER}>Other…</option>}
      </select>
      {custom && (
        <input
          aria-label={`${field.label} (other)`}
          value={value}
          autoComplete="off"
          placeholder={field.placeholder ?? 'Type it in'}
          className={control}
          onChange={(e) => onChange(e.target.value.trim())}
        />
      )}
    </div>
  );
}
